// Phase 8.WLNAV.v1 — pure client-side sorting + filtering for the Watchlist.
// These helpers never trigger network calls; they operate on the already
// loaded `items` array returned by GET /api/watchlist.

/* ---------------- Sort ---------------- */

// Stable secondary sort by ticker ASC when the primary key ties.
function _cmpTicker(a, b) {
  return String(a.ticker || "").localeCompare(String(b.ticker || ""));
}

// Rank valuation statuses so "Status" sort is meaningful:
//   Undervalued < Fairly valued < Overvalued
const STATUS_RANK = {
  undervalued: 0,
  fairly_valued: 1,
  overvalued: 2,
};
function _statusRank(s) {
  const k = String(s || "").toLowerCase().replace(/\s+/g, "_");
  return STATUS_RANK[k] ?? 99;
}

// Sort key extractors. Return null when the row lacks the field so we can
// push those rows to the bottom regardless of asc/desc direction.
const SORT_KEYS = {
  added_at:              (x) => x.added_at ? new Date(x.added_at).getTime() : null,
  ticker:                (x) => (x.ticker || "").toUpperCase(),
  name:                  (x) => (x.name || "").toLowerCase(),
  price:                 (x) => (x.price ?? null),
  fair_value:            (x) => (x.fair_value ?? null),
  upside_pct:            (x) => (x.upside_pct ?? null),
  ai_score:              (x) => (x.ai_score ?? null),
  status:                (x) => _statusRank(x.status),
  market_data_updated_at:(x) => x.market_data_updated_at ? new Date(x.market_data_updated_at).getTime() : null,
};

export const SORTABLE_KEYS = Object.keys(SORT_KEYS);

/**
 * Apply a stable sort. Default sort matches historical behavior:
 *   added_at DESC (most recently added first), ticker ASC on tie.
 *
 * @param {Array<object>} items
 * @param {{ key: string, dir: "asc" | "desc" }} sortState
 * @returns {Array<object>}
 */
export function applyWatchlistSort(items, sortState) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const list = items.slice();
  const key = (sortState && sortState.key) || "added_at";
  const dir = (sortState && sortState.dir) === "asc" ? 1 : -1;
  const extract = SORT_KEYS[key] || SORT_KEYS.added_at;

  list.sort((a, b) => {
    const va = extract(a);
    const vb = extract(b);
    // Null / missing values always sink to the bottom, regardless of dir.
    const aNull = va === null || va === undefined || Number.isNaN(va);
    const bNull = vb === null || vb === undefined || Number.isNaN(vb);
    if (aNull && bNull) return _cmpTicker(a, b);
    if (aNull) return 1;
    if (bNull) return -1;

    let cmp;
    if (typeof va === "string" && typeof vb === "string") cmp = va.localeCompare(vb);
    else cmp = va < vb ? -1 : va > vb ? 1 : 0;
    if (cmp === 0) return _cmpTicker(a, b);
    return cmp * dir;
  });
  return list;
}

/* ---------------- Filter ---------------- */

// Normalize the API status field into one of the three known buckets.
function normalizeStatusBucket(s) {
  const k = String(s || "").toLowerCase().replace(/\s+/g, "_");
  if (k.includes("under")) return "undervalued";
  if (k.includes("over")) return "overvalued";
  if (k.includes("fair")) return "fairly_valued";
  return null;
}

/**
 * Apply combined filters. AND across categories, OR within a category.
 *
 * @param {Array<object>} items
 * @param {{
 *   statuses?: string[],      // ["undervalued","fairly_valued","overvalued"]
 *   labels?: string[],        // any watchlist label string
 *   sectorEmojis?: string[],  // sector emoji glyph
 * }} filterState
 * @returns {Array<object>}
 */
export function applyWatchlistFilters(items, filterState) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const st = filterState || {};
  const wantStatuses = Array.isArray(st.statuses) && st.statuses.length > 0 ? new Set(st.statuses) : null;
  const wantLabels = Array.isArray(st.labels) && st.labels.length > 0 ? new Set(st.labels) : null;
  const wantSectors = Array.isArray(st.sectorEmojis) && st.sectorEmojis.length > 0 ? new Set(st.sectorEmojis) : null;
  if (!wantStatuses && !wantLabels && !wantSectors) return items;

  return items.filter((it) => {
    if (wantStatuses) {
      const bucket = normalizeStatusBucket(it.status);
      if (!bucket || !wantStatuses.has(bucket)) return false;
    }
    if (wantLabels) {
      const rowLabels = Array.isArray(it.labels) ? it.labels : [];
      const hit = rowLabels.some((l) => wantLabels.has(l));
      if (!hit) return false;
    }
    if (wantSectors) {
      const emoji = it.sector_emoji || null;
      if (!emoji || !wantSectors.has(emoji)) return false;
    }
    return true;
  });
}

/* ---------------- Freshness helpers ---------------- */

/**
 * Format a UTC ISO timestamp as a relative "X min ago" string. Falls back
 * to null when the input is missing or invalid. i18n is handled by the
 * caller via the `t` translator to keep this helper pure.
 *
 * @param {string|null|undefined} iso
 * @param {(key: string, params?: object) => string} t
 * @returns {string|null}
 */
export function formatRelativeAge(iso, t) {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const diffMs = Date.now() - dt.getTime();
  if (diffMs < 0) return t("common.just_now") || "just now";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return t("common.just_now") || "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return t("common.minutes_ago", { n: min }) || `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return t("common.hours_ago", { n: hr }) || `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return t("common.days_ago", { n: day }) || `${day}d ago`;
  return t("common.months_ago", { n: Math.floor(day / 30) }) || `${Math.floor(day / 30)}mo ago`;
}

/* ---------------- localStorage helpers ---------------- */

// Phase 8.WLNAV.v1.2 — per-user scoped sort persistence.
//
// Rationale: multiple users may share the same browser (family device,
// shared workstation) so a global localStorage key would leak user A's
// preference to user B. Keys are now scoped by user_id, with a dedicated
// key for anonymous / logged-out preview state.
//
//   • Signed-in users → `stockaily.watchlist.sort.<user_id>`
//   • Signed-out/preview → `stockaily.watchlist.sort.anonymous`
//
// Legacy pre-v1.2 key: `stockaily_wl_sort` (global). We migrate it into
// the current user's scoped key on first read and then delete it.
const LEGACY_SORT_KEY_STORAGE = "stockaily_wl_sort";
const LEGACY_FILTER_KEY_STORAGE = "stockaily_wl_filters";
const DEFAULT_SORT = Object.freeze({ key: "added_at", dir: "desc" });

function _sortStorageKey(userId) {
  const suffix = userId ? String(userId) : "anonymous";
  return `stockaily.watchlist.sort.${suffix}`;
}

function _isValidSort(p) {
  return !!(p && SORT_KEYS[p.key] && (p.dir === "asc" || p.dir === "desc"));
}

/**
 * Load the persisted sort for the given user, migrating from the legacy
 * global key if present. Returns `null` if the user has never set a
 * preference — the caller then applies the "Most recently added first"
 * default. Never throws; localStorage errors degrade to `null`.
 *
 * @param {string|null|undefined} userId
 * @returns {{key: string, dir: "asc" | "desc"} | null}
 */
export function loadPersistedSort(userId) {
  const key = _sortStorageKey(userId);
  try {
    // 1) One-time migration from the pre-v1.2 global key. We only apply
    //    it to the CURRENT user's scoped key — never to `.anonymous` —
    //    so signed-in users adopt their existing preference on first
    //    load and the shared key is then cleaned up for good.
    if (userId) {
      const legacyRaw = window.localStorage.getItem(LEGACY_SORT_KEY_STORAGE);
      if (legacyRaw) {
        try {
          const legacy = JSON.parse(legacyRaw);
          if (_isValidSort(legacy) && !window.localStorage.getItem(key)) {
            window.localStorage.setItem(key, JSON.stringify(legacy));
          }
        } catch { /* ignore malformed legacy */ }
        try { window.localStorage.removeItem(LEGACY_SORT_KEY_STORAGE); } catch { /* ignore */ }
      }
    }
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (_isValidSort(p)) return { key: p.key, dir: p.dir };
    // Corrupt or unsupported value → silently ignore + clean up.
    try { window.localStorage.removeItem(key); } catch { /* ignore */ }
  } catch { /* ignore */ }
  return null;
}

export function persistSort(sortState, userId) {
  if (!_isValidSort(sortState)) return;
  try {
    window.localStorage.setItem(_sortStorageKey(userId), JSON.stringify({ key: sortState.key, dir: sortState.dir }));
  } catch { /* ignore */ }
}

/**
 * Clear the persisted sort for the given user. Used by the
 * "Reset to default" action inside the Sort popover.
 */
export function clearPersistedSort(userId) {
  try { window.localStorage.removeItem(_sortStorageKey(userId)); } catch { /* ignore */ }
}

// Phase 8.WLNAV.v1.2 — Filter persistence is intentionally REMOVED.
//
// A "forgotten" active filter is a common footgun: users return the
// next day, don't remember they filtered by e.g. "High Conviction",
// and think their Watchlist rows have disappeared. Filters therefore
// remain page/session-scoped only; every fresh page load starts with
// no filters applied.
//
// We still ship a one-shot cleanup that deletes any leftover pre-v1.2
// filter key on module import to avoid stale state sneaking back in.
try {
  if (typeof window !== "undefined" && window.localStorage) {
    window.localStorage.removeItem(LEGACY_FILTER_KEY_STORAGE);
  }
} catch { /* ignore */ }

export const DEFAULT_SORT_STATE = DEFAULT_SORT;
export const EMPTY_FILTER_STATE = { statuses: [], labels: [], sectorEmojis: [] };

export function isFilterActive(f) {
  if (!f) return false;
  return (f.statuses || []).length + (f.labels || []).length + (f.sectorEmojis || []).length > 0;
}

export function countActiveFilters(f) {
  if (!f) return 0;
  return (f.statuses || []).length + (f.labels || []).length + (f.sectorEmojis || []).length;
}
